import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import {
  useGetOperationsQuery,
  useGetErrorOperationsQuery,
} from "../../store/apis/asyncOperationsApi";
import dayjs from "dayjs";
import Chip from "@mui/material/Chip";
import type { components } from "../../types/api";
import OrbLoader from "../common/OrbLoader";

interface AsyncOperationsTabProps {
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

type OperationStatus = components["schemas"]["OperationStatus"];
type OperationType = components["schemas"]["OperationType"];
type AsyncOperation = components["schemas"]["AsyncOperationResponse"];

const operationStatuses: OperationStatus[] = [
  "QUEUED",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "SKIPPED",
  "TIMEOUT",
];

const operationTypes: OperationType[] = [
  "INTERNAL_CAMPAIGN_SYNC",
  "AMAZON_REPORT_POLL",
  "HIVESTACK_REPORT_POLL",
  "STATUS_UPDATE",
];

const AsyncOperationsTab: React.FC<AsyncOperationsTabProps> = ({
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [statusFilter, setStatusFilter] = useState<OperationStatus | "">("");
  const [typeFilter, setTypeFilter] = useState<OperationType | "">("");

  const queryArgs = useMemo(
    () => ({
      page: page + 1,
      pageSize: rowsPerPage,
      status: statusFilter || undefined,
      type: typeFilter || undefined,
    }),
    [page, rowsPerPage, statusFilter, typeFilter]
  );

  const {
    data: operationsResponse,
    isLoading: isLoadingOperations,
    refetch: refetchOperations,
  } = useGetOperationsQuery(queryArgs, { skip: showOnlyErrors });

  const {
    data: errorOperationsResponse,
    isLoading: isLoadingErrors,
    refetch: refetchErrorOperations,
  } = useGetErrorOperationsQuery(
    {
      page: page + 1,
      pageSize: rowsPerPage,
    },
    { skip: !showOnlyErrors }
  );

  const activeResponse = showOnlyErrors
    ? errorOperationsResponse?.data
    : operationsResponse?.data;

  const items: AsyncOperation[] = activeResponse?.items ?? [];
  const total = activeResponse?.total ?? 0;

  const handleRefresh = () => {
    if (showOnlyErrors) {
      refetchErrorOperations();
    } else {
      refetchOperations();
    }
  };

  const renderStatusChip = (status: string) => {
    const colorMap: Record<
      string,
      "default" | "success" | "error" | "warning"
    > = {
      SUCCEEDED: "success",
      FAILED: "error",
      RUNNING: "warning",
      QUEUED: "default",
      SKIPPED: "default",
      TIMEOUT: "warning",
    };
    return (
      <Chip
        label={status}
        size="small"
        color={colorMap[status] ?? "default"}
        variant={colorMap[status] ? "filled" : "outlined"}
      />
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h3" gutterBottom>
            {t("processor.title", "Processor")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyErrors}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setShowOnlyErrors(event.target.checked);
                  onPageChange(0);
                }}
              />
            }
            label={t("processor.async.showErrorsOnly", "Show errors only")}
          />

          <TextField
            select
            size="small"
            label={t("processor.async.status", "Status")}
            value={statusFilter}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setStatusFilter(event.target.value as OperationStatus | "");
              onPageChange(0);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">
              {t("common.all", { defaultValue: "All" })}
            </MenuItem>
            {operationStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t("processor.async.type", "Type")}
            value={typeFilter}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setTypeFilter(event.target.value as OperationType | "");
              onPageChange(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">
              {t("common.all", { defaultValue: "All" })}
            </MenuItem>
            {operationTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={isLoadingOperations || isLoadingErrors}
            startIcon={
              isLoadingOperations || isLoadingErrors ? (
                <OrbLoader size="inline" />
              ) : (
                <RefreshIcon fontSize="small" />
              )
            }
            sx={{ textTransform: "none", borderRadius: 6 }}
          >
            {t("common.refresh", { defaultValue: "Refresh" })}
          </Button>
        </Box>

        <Paper
          sx={{
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {isLoadingOperations || isLoadingErrors ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <OrbLoader
                size="large"
                label={t("common.loading", { defaultValue: "Cargando…" })}
              />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t("processor.async.noData", "No async operations found")}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("processor.async.id", "ID")}</TableCell>
                    <TableCell>
                      {t(
                        "processor.async.internalCampaign",
                        "Internal Campaign"
                      )}
                    </TableCell>
                    <TableCell>{t("processor.async.type", "Type")}</TableCell>
                    <TableCell>
                      {t("processor.async.status", "Status")}
                    </TableCell>
                    <TableCell>
                      {t("processor.async.updatedAt", "Updated at")}
                    </TableCell>
                    <TableCell>
                      {t("processor.async.error", "Error message")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((op) => (
                    <TableRow key={op.id}>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {op.id}
                      </TableCell>
                      <TableCell>
                        {op.internalCampaignId || t("common.na", "N/A")}
                      </TableCell>
                      <TableCell>{op.type}</TableCell>
                      <TableCell>{renderStatusChip(op.status)}</TableCell>
                      <TableCell>
                        {dayjs(op.updatedAt).format("YYYY-MM-DD HH:mm")}
                      </TableCell>
                      <TableCell>
                        {op.errorMessage ? (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ maxWidth: 260 }}
                          >
                            {op.errorMessage}
                          </Typography>
                        ) : (
                          t("common.na", "N/A")
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_event, newPage) => onPageChange(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  onRowsPerPageChange(parseInt(event.target.value, 10));
                  onPageChange(0);
                }}
              />
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AsyncOperationsTab;
